import { Helmet } from 'react-helmet-async';

interface OrganizationSchemaProps {
  type?: 'Organization' | 'ProfessionalService';
}

export const OrganizationSchema = ({ type = 'Organization' }: OrganizationSchemaProps) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': type,
    name: 'Trouve un Médiateur',
    description: 'Plateforme de mise en relation avec des médiateurs de justice certifiés en France',
    url: 'https://trouveunmediateur.fr',
    logo: 'https://trouveunmediateur.fr/logo.png',
    sameAs: [],
    address: {
      '@type': 'PostalAddress',
      addressCountry: 'FR',
    },
    areaServed: {
      '@type': 'Country',
      name: 'France',
    },
    serviceType: 'Médiation',
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface BreadcrumbSchemaProps {
  items: BreadcrumbItem[];
}

export const BreadcrumbSchema = ({ items }: BreadcrumbSchemaProps) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

export const WebsiteSchema = () => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Trouve un Médiateur',
    url: 'https://trouveunmediateur.fr',
    description: 'Trouvez des médiateurs de justice certifiés en France',
    inLanguage: 'fr-FR',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://trouveunmediateur.fr/seeker?search={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

export const SpeakableSchema = ({ url, cssSelectors = ['h1', '.speakable'] }: { url: string; cssSelectors?: string[] }) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    url,
    speakable: {
      '@type': 'SpeakableSpecification',
      cssSelector: cssSelectors,
    },
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

interface HowToStep {
  name: string;
  text: string;
}

export const HowToSchema = ({ name, description, steps }: { name: string; description: string; steps: HowToStep[] }) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name,
    description,
    step: steps.map((step, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      name: step.name,
      text: step.text,
    })),
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

interface FAQItem {
  question: string;
  answer: string;
}

export const FAQSchema = ({ faqs }: { faqs: FAQItem[] }) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

export const VideoSchema = ({ name, description, thumbnailUrl, contentUrl, uploadDate }: { name: string; description: string; thumbnailUrl: string; contentUrl: string; uploadDate: string }) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name,
    description,
    thumbnailUrl,
    contentUrl,
    uploadDate,
    publisher: {
      '@type': 'Organization',
      name: 'Trouve un Médiateur',
      url: 'https://trouveunmediateur.fr',
    },
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};

export const AggregateRatingSchema = ({ ratingValue, reviewCount, bestRating = 5 }: { ratingValue: number; reviewCount: number; bestRating?: number }) => {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    name: 'Trouve un Médiateur',
    url: 'https://trouveunmediateur.fr',
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue,
      reviewCount,
      bestRating,
      worstRating: 1,
    },
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schema)}
      </script>
    </Helmet>
  );
};
